import "abort-controller/polyfill";
import cheerio from "cheerio";
import queryString from "query-string";
import "whatwg-fetch";

type SelectOperation = "update-query" | "replace-query" | "replace-url";

const AJAX_SELECT_OPERATIONS: Record<
  SelectOperation,
  (value: string, param: string) => string
> = {
  "update-query": (value: string, param: string) => {
    const existingQuery = queryString.parse(window.location.search);
    const newQuery = queryString.stringify({
      ...existingQuery,
      [param]: value || undefined,
    });
    return `${window.location.pathname}${newQuery ? "?" : ""}${newQuery}`;
  },
  "replace-query": (value: string, param: string) => {
    const newQuery = queryString.stringify({
      [param]: value || undefined,
    });
    return `${window.location.pathname}${newQuery ? "?" : ""}${newQuery}`;
  },
  "replace-url": (value: string) => {
    return value;
  },
};

const abortControllers: AbortController[] = [];

let getNewContent: (
  containerId: string,
  container: Element,
  url: string,
) => void;

const executeScripts = (container: Element) => {
  const scripts = container.querySelectorAll("[data-ajax-exec=true]");

  scripts.forEach((script) => {
    const parent = script.parentElement;
    const nextElement = script.nextSibling;
    script.remove();

    const newScript = document.createElement("script");
    newScript.textContent = script.textContent;

    script.getAttributeNames().forEach((attribute) => {
      newScript.setAttribute(attribute, script.getAttribute(attribute) || "");
    });

    if (parent) {
      parent.insertBefore(newScript, nextElement);
    }
  });
};

const isSelectOperation = (operation: string): operation is SelectOperation =>
  operation in AJAX_SELECT_OPERATIONS;

const updateUrlAndGetNewContent = (
  containerId: string,
  container: Element,
  newUrl: string,
) => {
  const title = document.getElementsByTagName("title")[0].textContent || "";

  window.history.pushState(null, title, newUrl);
  getNewContent(containerId, container, newUrl);
};

const onClickLink = (containerId: string, container: Element, event: Event) => {
  event.preventDefault();

  if (!(event.currentTarget instanceof HTMLAnchorElement)) {
    throw new Error("Event target was not a link");
  }

  if (!event.currentTarget.href) {
    throw new Error("Event target did not contain href");
  }

  updateUrlAndGetNewContent(containerId, container, event.currentTarget.href);
};

const addLinkHandlers = (containerId: string, container: Element) => {
  const ajaxLinks = [...container.querySelectorAll("[data-ajax-link=true]")];

  ajaxLinks.forEach((ajaxLink) => {
    ajaxLink.addEventListener("click", (event) =>
      onClickLink(containerId, container, event),
    );
  });
};

const onChangeSelect = (
  containerId: string,
  container: Element,
  operation: SelectOperation,
  param: string,
  event: Event,
) => {
  if (!(event.currentTarget instanceof HTMLSelectElement)) {
    throw new Error("Event target was not a select");
  }

  const value = event.currentTarget.value;
  const newUrl = AJAX_SELECT_OPERATIONS[operation](value, param);

  updateUrlAndGetNewContent(containerId, container, newUrl);
};

const addSelectHandlers = (containerId: string, container: Element) => {
  const ajaxSelects = [
    ...container.querySelectorAll("[data-ajax-select=true]"),
  ];

  ajaxSelects.forEach((ajaxSelect) => {
    const operation = ajaxSelect.getAttribute("data-ajax-operation") || "";
    const param = ajaxSelect.getAttribute("data-ajax-param") || "";

    if (!isSelectOperation(operation)) {
      throw new Error(`Invalid ajax select operation: "${operation}"`);
    }

    if (operation !== "replace-url" && !param) {
      throw new Error(
        `Could not get ajax-select-param for operation "${operation}"`,
      );
    }

    ajaxSelect.addEventListener("change", (event) =>
      onChangeSelect(containerId, container, operation, param, event),
    );
  });
};

getNewContent = (containerId: string, container: Element, url: string) => {
  while (abortControllers.length) {
    const abortControllerToAbort = abortControllers.shift();

    if (abortControllerToAbort) {
      abortControllerToAbort.abort();
    }
  }

  const abortController = new AbortController();
  const signal = abortController.signal;

  abortControllers.push(abortController);

  container.classList.add("ajaxify-loading");

  window
    .fetch(url, { signal })
    .then((response) => response.text())
    .then((html) => {
      const newContent = cheerio(html).find(`#${containerId}`).html();

      if (!newContent) {
        throw new Error("New document did not contain the required content");
      }

      container.innerHTML = newContent;

      addLinkHandlers(containerId, container);
      addSelectHandlers(containerId, container);
      executeScripts(container);
    })
    .catch((error) => {
      if (error.name !== "AbortError" && window.console) {
        // eslint-disable-next-line no-console
        console.error((error && error.message) || error);
      }
    })
    .then(() => {
      const abortControllerIndex = abortControllers.indexOf(abortController);

      if (abortControllerIndex >= 0) {
        abortControllers.splice(abortControllerIndex, 1);
      }

      if (!abortControllers.length) {
        container.classList.remove("ajaxify-loading");
      }
    });
};

const addHistoryPopHandler = (containerId: string, container: Element) => {
  window.addEventListener("popstate", () =>
    getNewContent(containerId, container, window.location.href),
  );
};

const init = () => {
  const thisScriptTag = document.querySelector("script[data-container-id]");

  if (!thisScriptTag) {
    throw new Error("Could not access ajaxify script tag");
  }

  const containerId = thisScriptTag.getAttribute("data-container-id");

  if (!containerId) {
    throw new Error("No container ID specified on ajaxify script tag");
  }

  const container = document.querySelector(`#${containerId}`);

  if (!container) {
    throw new Error(`Could not find a container with the ID: "${containerId}"`);
  }

  addLinkHandlers(containerId, container);
  addSelectHandlers(containerId, container);
  addHistoryPopHandler(containerId, container);
};

if (window.history) {
  document.addEventListener("DOMContentLoaded", () => init());
}
